import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import { useDetailsModal } from '../context/DetailsModalContext';
import { formatDateDMY } from '../utils/dateUtils';
import { toCurrency } from '../utils/adminMetrics';
import { useSortableTable, TABLE_HEAD_ROW, TABLE_HEAD_TEXT, TABLE_ROW_LINE } from './UI/tableSort';

// A loan's debit order collections (staff): the amount the next file asks
// for and each collection requested, collected or missed. The API works it
// out (GET /api/debit-orders/loan/:id); the instalment itself never changes.

const OUTCOME = {
  collected: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  missed: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  pending: 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300',
};

const DebitOrderCollections = ({ loanId }) => {
  const { isClientUser } = useDetailsModal();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!loanId || isClientUser) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(apiUrl(`/api/debit-orders/loan/${loanId}`), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled && err?.response?.status !== 403) setError(err?.response?.data?.error || 'The collections could not be loaded.');
      }
    })();
    return () => { cancelled = true; };
  }, [loanId, isClientUser]);

  // The latest deduction first until a column header is clicked.
  const history = useSortableTable((data?.history || []).slice().reverse());
  const hc = { className: `px-3 py-1.5 ${TABLE_HEAD_TEXT}` };

  if (isClientUser || (!data && !error)) return null;
  if (error) return <div className="text-xs text-red-600 dark:text-red-400">{error}</div>;

  const changed = data.next_amount !== data.instalment;
  return (
    <div>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <div>
          <span className="text-xs text-gray-500 dark:text-slate-400">Next debit amount </span>
          <span className={`font-semibold ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-900 dark:text-slate-100'}`}>{toCurrency(data.next_amount)}</span>
        </div>
        <div className="text-xs text-gray-500 dark:text-slate-400">Instalment {toCurrency(data.instalment)}</div>
      </div>
      {data.note && (
        <div className={`mt-1 text-xs ${data.mode === 'manual' ? 'text-red-700 dark:text-red-300 font-medium' : 'text-amber-800 dark:text-amber-300'}`}>{data.note}</div>
      )}
      {data.history.length > 0 ? (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className={TABLE_HEAD_ROW}>
                {history.th('Deduction', 'deduction', (h) => h.deduction_date, hc)}
                {history.th('Collected on', 'collected', (h) => h.collection_date, hc)}
                {history.th('Asked', 'asked', (h) => Number(h.amount), { ...hc, align: 'right' })}
                {history.th('Received', 'received', (h) => Number(h.received), { ...hc, align: 'right' })}
                {history.th('Result', 'result', (h) => h.outcome, hc)}
              </tr>
            </thead>
            <tbody>
              {history.rows.map((h) => (
                <tr key={h.deduction_date} className={TABLE_ROW_LINE}>
                  <td className="px-3 py-1.5">{formatDateDMY(h.deduction_date)}</td>
                  <td className="px-3 py-1.5">{formatDateDMY(h.collection_date)}</td>
                  <td className="px-3 py-1.5 text-right">{toCurrency(h.amount)}</td>
                  <td className="px-3 py-1.5 text-right">{toCurrency(h.received)}</td>
                  <td className="px-3 py-1.5">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${OUTCOME[h.outcome] || OUTCOME.pending}`}>{h.outcome}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-1 text-xs text-gray-500 dark:text-slate-400">No debit orders requested yet.</div>
      )}
    </div>
  );
};

export default DebitOrderCollections;
